import React, { useEffect } from "react";

import useOnScreen from "./useOnScreen";
import usePreviousValue from "./usePreviousValue";
import { useEventListener } from "./useEventListener";

/**
 * Calls the provided callback once when a node transitions from visible to not
 * visible in the viewport, or when the document itself is hidden via e.g. a tab switch.
 */
export default function useWhenLeavingScreenCallback(
  node: Element | null | undefined,
  callback: () => void,
  options?: IntersectionObserverInit,
) {
  const isOnScreen = useOnScreen(node, false, options);
  const wasOnScreen = usePreviousValue(isOnScreen);
  const callbackRef = React.useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  React.useEffect(() => {
    if (wasOnScreen && !isOnScreen) {
      callbackRef.current();
    }
  }, [isOnScreen, wasOnScreen]);

  useEventListener(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      callbackRef.current();
    }
  });
}
